import { useEffect, useState } from "react";
import {
  Calculator, PlayCircle, Send, Undo2, Trash2, RefreshCw, AlertTriangle, Users, Banknote, Building2, Eye, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { SlipRincian } from "@/components/SlipRincian";
import { useAuth } from "@/context/AuthContext";
import {
  api, apiErrorMessage, formatRupiah, formatTanggalID, windowPeriodePayroll, API, getToken,
} from "@/lib/api";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export default function AdminPayrollRun() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const now = new Date();
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [runs, setRuns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dilewati, setDilewati] = useState([]);
  const [cutoff, setCutoff] = useState(1);
  const [slipDetail, setSlipDetail] = useState(null);
  const [komponenMap, setKomponenMap] = useState({});

  const periode = `${tahun}-${String(bulan).padStart(2, "0")}`;
  const win = windowPeriodePayroll(periode, cutoff);

  useEffect(() => {
    api.get("/settings/payroll")
      .then(({ data }) => setCutoff(data.cutoff_start_day || 1))
      .catch(() => {});
    // Load komponen master utk tooltip Grup Laporan di SlipRincian
    api.get("/payroll/komponen")
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((k) => {
          m[k.id] = {
            nama: k.nama,
            grup_nama: k.grup_laporan,
            grup_warna: k.grup_laporan_warna || "slate",
          };
        });
        setKomponenMap(m);
      })
      .catch(() => {});
  }, []);

  async function loadRuns(selectPeriode) {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/run");
      setRuns(data);
      const target = data.find((r) => r.periode === (selectPeriode || periode)) || data[0];
      if (target) await loadDetail(target.id);
      else setDetail(null);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadRuns(); /* eslint-disable-next-line */ }, []);

  async function downloadSlipPdf(s) {
    const token = getToken();
    const url = `${API}/payroll/slip/${s.id}/pdf`;
    try {
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal mengunduh slip gaji");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const nama = (s.nama || "pegawai").replace(/\s+/g, "_");
      a.download = `SlipGaji_${nama}_${s.periode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Slip gaji berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal mengunduh slip gaji");
    }
  }

  async function loadDetail(rid) {
    try {
      const { data } = await api.get(`/payroll/run/${rid}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function hitung() {
    setBusy(true);
    setDilewati([]);
    try {
      const { data } = await api.post("/payroll/run", { periode });
      toast.success(`Payroll ${data.run.periode_label} dihitung — ${data.dihitung} pegawai (Draft)`);
      setDilewati(data.dilewati || []);
      await loadRuns(periode);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function aksi(path, pesanSukses) {
    if (!detail?.run) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/run/${detail.run.id}/${path}`);
      toast.success(data.pesan || pesanSukses);
      await loadRuns(detail.run.periode);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function hapus() {
    if (!detail?.run) return;
    if (!window.confirm(`Hapus hasil perhitungan payroll ${detail.run.periode_label}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/payroll/run/${detail.run.id}`);
      toast.success("Hasil perhitungan dihapus");
      setDetail(null);
      await loadRuns();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const run = detail?.run;
  const slips = detail?.slips || [];
  const terbit = run?.status === "Terbit";

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-payroll-run">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Run Payroll
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Hitung gaji seluruh pegawai dari data absensi &amp; lembur aktual, lalu terbitkan agar slip terlihat pegawai.
          </p>
        </div>
        <Button variant="outline" onClick={() => loadRuns(run?.periode)} disabled={loading} className="gap-2" data-testid="run-refresh-btn">
          <RefreshCw size={14} /> Muat Ulang
        </Button>
      </div>

      {/* Pemilih periode */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan Gaji</Label>
            <Select value={bulan} onValueChange={setBulan}>
              <SelectTrigger className="mt-1.5" data-testid="run-bulan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BULAN.map((b, i) => (<SelectItem key={b} value={String(i + 1)}>{b}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tahun</Label>
            <Select value={tahun} onValueChange={setTahun}>
              <SelectTrigger className="mt-1.5" data-testid="run-tahun"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2].map((d) => {
                  const y = String(now.getFullYear() - d);
                  return <SelectItem key={y} value={y}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <Button onClick={hitung} disabled={busy} className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="run-hitung-btn">
              <PlayCircle size={15} /> {busy ? "Memproses…" : "Hitung Payroll"}
            </Button>
            {runs.some((r) => r.periode === periode) ? (
              <span className="text-[11px] text-amber-700 self-center">
                Periode ini sudah pernah dihitung — tombol di atas akan menghitung ulang (Draft).
              </span>
            ) : null}
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-teal-100 bg-teal-50/60 px-4 py-3 text-xs text-teal-900" data-testid="run-periode-preview">
          <b>Payroll {BULAN[Number(bulan) - 1]} {tahun}</b> — cutoff{" "}
          {win ? `${formatTanggalID(win.start)} – ${formatTanggalID(win.end)}` : "—"}.
          <span className="block mt-0.5 text-teal-800/80">
            Nama periode mengikuti bulan tanggal akhir cutoff (bulan gaji), bukan bulan awal cutoff.
          </span>
        </div>
      </div>

      {dilewati.length > 0 ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="run-dilewati">
          <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
            <AlertTriangle size={15} /> {dilewati.length} pegawai dilewati
          </div>
          <ul className="mt-2 text-xs text-amber-900 space-y-1">
            {dilewati.map((d, i) => (<li key={i}>• {d.nama} ({d.npp}) — {d.alasan}</li>))}
          </ul>
        </div>
      ) : null}

      {/* Hasil perhitungan */}
      {run ? (
        <div className="mt-6" data-testid="run-detail">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-xl font-extrabold text-slate-900">{run.periode_label}</h2>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                    terbit ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-amber-50 text-amber-800 border-amber-100"
                  }`} data-testid="run-status">
                    {run.status}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Cutoff {formatTanggalID(run.periode_start)} – {formatTanggalID(run.periode_end)} · Gajian{" "}
                  {formatTanggalID(run.tanggal_gajian)} · Dihitung oleh {run.dihitung_oleh_nama || "—"}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {terbit ? (
                  isAdmin ? (
                    <Button variant="outline" onClick={() => aksi("batalkan-terbit")} disabled={busy} className="gap-1.5" data-testid="run-batalkan-btn">
                      <Undo2 size={14} /> Batalkan Terbit
                    </Button>
                  ) : null
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!window.confirm(`Hitung ulang seluruh slip payroll ${detail.run.periode_label}? Perhitungan lama akan diganti.`)) return;
                        aksi("recalculate", "Payroll berhasil dihitung ulang");
                      }}
                      disabled={busy}
                      className="gap-1.5 border-teal-200 text-teal-800 hover:bg-teal-50"
                      data-testid="run-recalculate-btn"
                      title="Hitung ulang seluruh slip pada run ini (aman selama masih Draft)."
                    >
                      <RefreshCw size={14} /> Hitung Ulang
                    </Button>
                    <Button onClick={() => aksi("terbitkan")} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="run-terbitkan-btn">
                      <Send size={14} /> Terbitkan Slip
                    </Button>
                    {isAdmin ? (
                      <Button variant="outline" onClick={hapus} disabled={busy} className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="run-hapus-btn">
                        <Trash2 size={14} /> Hapus
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 lg:grid-cols-6 gap-3">
              <Stat icon={Users} label="Pegawai" value={run.jumlah_pegawai} plain />
              <Stat icon={Banknote} label="Total Bruto" value={formatRupiah(run.total_bruto)} />
              <Stat icon={Calculator} label="Total Potongan" value={formatRupiah(run.total_potongan)} tone="rose" />
              <Stat icon={Calculator} label="PPh 21" value={formatRupiah(run.total_pph21)} tone="amber" />
              <Stat icon={Building2} label="Beban Perusahaan" value={formatRupiah(run.total_beban_perusahaan)} tone="indigo" />
              <Stat icon={Banknote} label="Total Netto" value={formatRupiah(run.total_netto)} tone="teal" />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {["Pegawai", "Unit", "Hadir/Kerja", "Alpha", "Lembur", "Gaji Pokok", "Tetap", "Tidak Tetap", "Bruto", "Potongan", "PPh 21", "Beban Prsh", "Netto", "Rincian"].map((h) => (
                      <TableHead key={h} className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slips.map((s) => (
                    <TableRow key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`run-slip-${s.id}`}>
                      <TableCell>
                        <div className="font-semibold text-slate-900">{s.nama}</div>
                        <div className="text-[11px] text-slate-500 font-mono-clock">{s.npp}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{s.unit_nama}</TableCell>
                      <TableCell className="text-sm text-slate-700 font-mono-clock">{s.hari_hadir}/{s.hari_kerja_periode}</TableCell>
                      <TableCell className="text-sm font-mono-clock">
                        {s.hari_alpha > 0 ? <span className="text-rose-700 font-semibold">{s.hari_alpha}</span> : <span className="text-slate-400">0</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700 font-mono-clock">{s.total_jam_lembur} j</TableCell>
                      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{formatRupiah(s.gaji_pokok)}</TableCell>
                      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{formatRupiah((s.bruto_tetap || 0) - (s.gaji_pokok || 0))}</TableCell>
                      <TableCell className="text-sm text-sky-700 whitespace-nowrap">{formatRupiah(s.bruto_tidak_tetap)}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-700 whitespace-nowrap">{formatRupiah(s.bruto)}</TableCell>
                      <TableCell className="text-sm text-rose-700 whitespace-nowrap">{formatRupiah(s.total_potongan)}</TableCell>
                      <TableCell className="text-sm text-amber-800 whitespace-nowrap">
                        {formatRupiah(s.pph21_dipotong)}
                        {s.pph21_ditanggung_perusahaan ? (
                          <span className="block text-[10px] text-indigo-700">
                            {formatRupiah(s.pph21_ditanggung_perusahaan)} oleh prsh.
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm text-indigo-700 whitespace-nowrap">{formatRupiah(s.total_beban_perusahaan)}</TableCell>
                      <TableCell className="text-sm font-extrabold text-teal-800 whitespace-nowrap">{formatRupiah(s.netto)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 h-8"
                            onClick={() => setSlipDetail(s)}
                            data-testid={`run-slip-detail-${s.id}`}
                          >
                            <Eye size={13} /> Rincian
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 h-8 text-teal-700 border-teal-200 hover:bg-teal-50"
                            onClick={() => downloadSlipPdf(s)}
                            data-testid={`run-slip-pdf-${s.id}`}
                            title="Download Slip Gaji PDF"
                          >
                            <FileDown size={13} /> PDF
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="mt-8 text-center text-slate-500 py-10">Memuat…</div>
      ) : (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="run-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <Calculator size={22} />
          </div>
          <div className="mt-3 font-semibold text-slate-700">Belum ada perhitungan payroll</div>
          <div className="text-xs text-slate-500 mt-1">Pilih periode di atas lalu klik "Hitung Payroll".</div>
        </div>
      )}

      {/* Riwayat run */}
      {runs.length > 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="run-history">
          <h3 className="text-sm font-extrabold text-slate-800">Riwayat Perhitungan</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {runs.map((r) => (
              <button
                key={r.id}
                onClick={() => loadDetail(r.id)}
                className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                  run?.id === r.id
                    ? "bg-teal-50 border-teal-200 text-teal-800"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`run-chip-${r.periode}`}
              >
                {r.periode_label} · {r.status} · {formatRupiah(r.total_netto)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <Dialog open={!!slipDetail} onOpenChange={(o) => !o && setSlipDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="run-slip-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">
              Rincian Gaji — {slipDetail?.nama}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {slipDetail?.npp} · {slipDetail?.unit_nama} · {slipDetail?.periode_label}
              {slipDetail?.periode_rentang ? ` (cutoff ${slipDetail.periode_rentang})` : ""}
            </DialogDescription>
          </DialogHeader>
          {slipDetail ? (
            <div className="flex justify-end -mt-1">
              <Button
                size="sm"
                className="bg-teal-700 hover:bg-teal-800 gap-1.5"
                onClick={() => downloadSlipPdf(slipDetail)}
                data-testid="run-slip-dialog-pdf"
              >
                <FileDown size={13} /> Download Slip Gaji (PDF)
              </Button>
            </div>
          ) : null}
          <SlipRincian slip={slipDetail} showBeban testId="run-slip-rincian" komponenMap={komponenMap} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, plain }) {
  const tones = {
    rose: "text-rose-700 border-rose-200 bg-rose-50",
    amber: "text-amber-800 border-amber-200 bg-amber-50",
    indigo: "text-indigo-700 border-indigo-200 bg-indigo-50",
    teal: "text-teal-800 border-teal-200 bg-teal-50",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className={`w-8 h-8 rounded-md border flex items-center justify-center ${tones[tone] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
        <Icon size={14} />
      </div>
      <div className={`mt-2 font-extrabold text-slate-900 leading-tight ${plain ? "text-2xl" : "text-base"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-0.5">{label}</div>
    </div>
  );
}
